import { BookingCompressed, BookingRequest, BookingRequestVisibilityEnum } from '@jooxter/api';
import { useCallback, useMemo } from 'react';
import { useFetchBookings } from '../queries/queryFns';

// Canonical version of this adapter - previously duplicated as
// packages/core/src/adapters/bookings/useBookingCompressedToBookingRequestAdapter.ts, which has been
// removed in favour of this one so both @jooxter/core and @jooxter/bookings consumers share it.
export const useBookingCompressedToBookingRequestAdapter = (bookings?: BookingCompressed[]) => {
  const bookingIds = useMemo(() => bookings?.map((b) => b.id) ?? [], [bookings]);
  const { bookings: bookingsFromApi, isPending } = useFetchBookings(bookingIds);

  const adapt = useCallback(
    (booking: BookingCompressed): BookingRequest | undefined => {
      const bookingFromApi = bookingsFromApi.find((b) => b.id === booking.id);

      if (!bookingFromApi) {
        return;
      }

      const bookingRequest: BookingRequest = {
        summary: bookingFromApi.summary,
        description: bookingFromApi.description,
        start: bookingFromApi.start,
        end: bookingFromApi.end,
        color: bookingFromApi.color,
        organizerId: bookingFromApi.organizer.id,
        resourceId: bookingFromApi.resource.id,
        visibility: bookingFromApi.visibility as unknown as BookingRequestVisibilityEnum,
        participationStatusAttendees: [],
        physicalAttendees: bookingFromApi.physicalAttendees,
        options: bookingFromApi.options.map((option) => ({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          optionOverloadedId: option.optionId!,
          value: option.value?.content ?? '',
        })),
        attendees: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          internal: bookingFromApi.attendees.internal.map((internal) => internal.id!),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          external: bookingFromApi.attendees.external.map((external) => external.id!),
        },
      };

      return bookingRequest;
    },
    [bookingsFromApi]
  );

  // Exposed so the caller can hold off confirming a Truncate choice until every candidate booking's
  // full record has arrived - `adapt` silently returns undefined for a booking that hasn't loaded yet,
  // which would otherwise let a truncate be confirmed and then skipped as a no-op.
  return { adapt, isLoading: isPending };
};
